import { get, isUndefined } from 'lodash/fp';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Spinner,
} from 'reactstrap';

import { useEditDeviceModel } from '@portals/api/partners';
import { AutoFormik } from '@portals/autoformik';
import { usePermissionAccess } from '@portals/framework';
import { FieldTypeEnum, StateType } from '@portals/types';

import {
  fetchIntegrations,
  getIntegrationParam,
} from '../../../redux/actions/integrations';

const MAP_OPTIONS = {
  zoho: 'products',
};

const ModelIntegrations = ({
  model,
  integrations,
  fetchIntegrations,
  getIntegrationParam,
}) => {
  const { canEdit } = usePermissionAccess();
  const editDeviceModel = useEditDeviceModel();

  useEffect(fetchIntegrations, [fetchIntegrations]);

  const fields = useMemo(
    () =>
      Object.keys(integrations).map((integrationKey: string) => ({
        name: integrationKey,
        title: integrationKey,
        clearable: true,
        type: FieldTypeEnum.Select,
        options: get(
          [integrationKey, 'extra', MAP_OPTIONS[integrationKey]],
          integrations
        ),
      })),
    [integrations]
  );

  const handleSubmit = (values) =>
    editDeviceModel.mutate({
      id: model.id,
      user_settings: { ...model.user_settings, integrations: values },
    });

  const onIntegrationRefetch = () =>
    Object.keys(integrations).forEach((integration) =>
      getIntegrationParam(integration, MAP_OPTIONS[integration])
    );

  const refreshOptions = () => (
    <Button onClick={onIntegrationRefetch}>Reload options</Button>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="h5">Integrations Data</CardTitle>
        <h6 className="text-muted">
          Mapping between model and integration data
        </h6>
      </CardHeader>
      <CardBody>
        {isUndefined(integrations) && <Spinner />}

        {!isUndefined(integrations) && Object.keys(integrations).length === 0 && (
          <span>
            You must enable an{' '}
            <Link to="/settings/integrations">integration</Link>
          </span>
        )}

        {!isUndefined(integrations) && Object.keys(integrations).length > 0 && (
          <AutoFormik
            readOnly={!canEdit('models')}
            fields={fields}
            initialValues={model.user_settings.integrations}
            inProgress={editDeviceModel.isLoading}
            handleSubmit={handleSubmit}
            wrapped={refreshOptions}
          />
        )}
      </CardBody>
    </Card>
  );
};

const mapStateToProps = (state: StateType, ownProps) => ({
  integrations: state.data.integrations || {},
});

export default connect(mapStateToProps, {
  fetchIntegrations,
  getIntegrationParam,
})(ModelIntegrations);
